/**
 * @documental
 * @export Dropdown
 */
import { ReactNode, forwardRef } from "react";
import { SelectInstance } from "react-select";

import { withSubComponents } from "../../utilities/withSubComponents";
import { InputBaseProps } from "../InputBaseProps";
import { InputChangeHandler } from "../InputChangeHandler";
import { RefComponent } from "../RefComponent";
import { CustomReactSelectProps } from "./CustomReactSelectProps";
import { Field } from "./Field";
import { Group } from "./Group";
import { Input } from "./Input";
import { Option } from "./Option";
import { OptionItem } from "./OptionItem";
import { Placeholder } from "./Placeholder";

/**
 * The `Dropdown` component properties.
 */
export interface DropdownProps extends InputBaseProps<string | number | null> {
  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The options to display.
   */
  readonly children?: ReactNode;

  /**
   * The placeholder text.
   */
  readonly placeholder?: string;

  /**
   * Indicates whether the dropdown is able to be cleared. If not set, defaults to `false`.
   */
  readonly clearable?: boolean;

  /**
   * Indicates whether the dropdown is able to be searched. If not specified defaults to `false`.
   */
  readonly searchable?: boolean;

  /**
   * Props for search input when using searchable.
   */
  readonly searchInputProps?: CustomReactSelectProps["searchInputProps"];

  /**
   * Message to display when no options are returned from search.
   */
  readonly noOptionsMessage?: ReactNode;

  /**
   * Indicates that the behavior of search is managed manually.
   */
  readonly manualSearchFilter?: boolean;

  /**
   * Called when the search value changes.
   */
  readonly onSearchChange?: InputChangeHandler<string | null>;

  /**
   * Called when the menu is scrolled to the bottom.
   */
  readonly onMenuScrollToBottom?: () => void;
}

/**
 * The `Dropdown` component sub components.
 */
export interface DropdownSubComponents {
  /**
   * A React Hook Form compatible Dropdown component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * Represents a single option which can be placed within the Dropdown component.
   */
  readonly Option: typeof Option;

  /**
   * A placeholder component for the Dropdown which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;

  /**
   * Represents a group of options.
   */
  readonly Group: typeof Group;
}

/**
 * A dropdown component.
 *
 * `import { Dropdown } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>To use the React Hook Form version add the following use the `Field` sub component:</strong>
 *
 * `<Dropdown.Field .../>`
 *
 * <strong>To use the regular version of the TextArea</strong>
 *
 * `<Dropdown .../>`
 *
 * The regular version acts like a normal field, in order to make it work
 * you need to pass in a `value` and an `onChange` for it to be interactive
 *
 * ```typescript
  const [value, onChange] = useSimpleInputState<string | null>(null);

  return (
    <Dropdown
      label="None RHF Dropdown"
      placeholder="Sample Text"
      onChange={(newValue, prev) => onChange(newValue, prev)}
      value={value}
      instructions="Sample instructions"
    >
      <Dropdown.Option value="Finance">Finance</Dropdown.Option>
      <Dropdown.Option value="Development">Development</Dropdown.Option>
    </Dropdown>
  );
  ```
 */
export const Dropdown = withSubComponents<RefComponent<SelectInstance<OptionItem | null, boolean>, DropdownProps>, DropdownSubComponents>(
  {
    Field,
    Option,
    Placeholder,
    Group,
  },
  forwardRef<SelectInstance<OptionItem | null, boolean>, DropdownProps>(function Dropdown(props, ref) {
    const { children } = props;

    return (
      <Input {...props} ref={ref}>
        {children}
      </Input>
    );
  }),
);
